// useSiteItems — PowerSync-backed hook for the per-site capture
// items rendered by SiteCaptureScreen.
//
// Site-level items live on the same `capture_items` table as the
// per-room counts but are distinguished by their location columns:
//   capture_items.building_id IS NULL AND capture_items.space_id IS NULL
// AND
//   asset_types.grouping_convention = '1 asset / site'
//
// JOIN chain: capture_items → asset_types → asset_sub_systems →
// asset_systems. The output shape mirrors `CaptureItemRow` from
// `@citycare/shared` so the existing `<AssetRow>` component renders
// without changes.
//
// Template synthesis (H16 / #709 — the load-bearing complication):
// The REST endpoint synthesises a "template-only" row for every active
// `1 asset / site` asset type the session has NOT yet captured, so the
// surveyor sees and can count configured site infrastructure on first
// open (get-session.ts → loadSiteTemplateAssetTypeIds + merge-template.ts
// → assembleSessionDetail's site branch). Offline that merge has to
// happen client-side. This hook does it in JS over two local queries,
// mirroring `useSessionDetail`'s building-scoped precedent:
//
//   1. capture_items  — the surveyor's actual persisted site rows.
//   2. asset_types / asset_sub_systems / asset_systems — every active
//      `1 asset / site` type (the site template).
//
// For each template type with no persisted row we emit a synthesised
// `isTemplateOnly: true` row (id ''); persisted rows suppress their
// template slot (invariant I-API-15). The merged list is sorted by
// (systemName, subSystemName, assetTypeName) via `compareSiteItemRows`
// to match the server's `compareItemRows` ordering exactly — JS
// `localeCompare`, NOT SQLite's binary `ORDER BY`, so accents/case sort
// identically to the backend.
//
// Contract scope: the server filters the site template by the active
// contract (asset_systems.contract_id). Phase 1 is single-tenant
// (PRD §82) and reference data syncs contract-global, so the local
// mirror only ever holds the active contract's asset_systems — the same
// reason `useSessionDetail` omits a client-side contract filter. The
// `rejected_at IS NULL` filters on all three classification levels are
// preserved so an admin-soft-deleted type / sub-system / system never
// surfaces a template slot (mirrors get-session.ts:296-315).

import { useMemo } from 'react'

import { useQuery } from '@powersync/react'

import type {
  CaptureItemRow,
  GroupingConvention,
  UnitOfMeasure,
} from '@citycare/shared'
import { GROUPING_CONVENTION } from '@citycare/shared'

interface SiteItemSqlRow {
  id: string
  asset_type_id: string
  asset_type_name: string
  unit_of_measure: string
  grouping_convention: string
  is_field_added: number
  requires_review: number
  sub_system_id: string
  sub_system_name: string
  system_id: string
  system_name: string
  quantity: string
  is_zero_confirmed: number
  notes: string | null
  captured_at: string
  updated_at: string
  sync_version: number
}

// Template-type row shape: the same classification columns as a
// persisted row, minus the capture_items columns (a template slot has
// no persisted count yet).
interface SiteTemplateSqlRow {
  asset_type_id: string
  asset_type_name: string
  unit_of_measure: string
  grouping_convention: string
  is_field_added: number
  requires_review: number
  sub_system_id: string
  sub_system_name: string
  system_id: string
  system_name: string
}

export interface UseSiteItemsResult {
  siteItems: CaptureItemRow[]
  isLoading: boolean
  isFetching: boolean
  error: Error | undefined
}

const IDLE: UseSiteItemsResult = {
  siteItems: [],
  isLoading: false,
  isFetching: false,
  error: undefined,
}

function projectItem(row: SiteItemSqlRow): CaptureItemRow {
  return {
    id: row.id,
    assetTypeId: row.asset_type_id,
    assetTypeName: row.asset_type_name,
    unitOfMeasure: row.unit_of_measure as UnitOfMeasure,
    groupingConvention: row.grouping_convention as GroupingConvention,
    systemId: row.system_id,
    systemName: row.system_name,
    subSystemId: row.sub_system_id,
    subSystemName: row.sub_system_name,
    quantity: row.quantity,
    isZeroConfirmed: row.is_zero_confirmed === 1,
    notes: row.notes,
    capturedAt: row.captured_at,
    updatedAt: row.updated_at,
    syncVersion: row.sync_version,
    isFieldAdded: row.is_field_added === 1,
    requiresReview: row.requires_review === 1,
    isTemplateOnly: false,
  }
}

// Synthesise a template-only row for an un-captured site type. Mirrors
// the server's `syntheticTemplateOnlyRow` (merge-template.ts:151-181)
// and `useSessionDetail`'s `projectTemplateOnly`: id '' (the client must
// createItem on first write, never PUT this id), quantity '0',
// syncVersion 0, isTemplateOnly true. `capturedAt` / `updatedAt` are
// empty presentation placeholders (a template-only row has no capture
// clock and AssetRow never renders them — matching the sibling
// building-scoped hook).
function projectTemplateOnly(row: SiteTemplateSqlRow): CaptureItemRow {
  return {
    id: '',
    assetTypeId: row.asset_type_id,
    assetTypeName: row.asset_type_name,
    unitOfMeasure: row.unit_of_measure as UnitOfMeasure,
    groupingConvention: row.grouping_convention as GroupingConvention,
    systemId: row.system_id,
    systemName: row.system_name,
    subSystemId: row.sub_system_id,
    subSystemName: row.sub_system_name,
    quantity: '0',
    isZeroConfirmed: false,
    notes: null,
    capturedAt: '',
    updatedAt: '',
    syncVersion: 0,
    isFieldAdded: row.is_field_added === 1,
    requiresReview: row.requires_review === 1,
    isTemplateOnly: true,
  }
}

// Ordering parity with the server's `compareItemRows` (merge-template.ts:102):
// system → sub-system → asset-type name, all via `localeCompare`.
export function compareSiteItemRows(
  a: CaptureItemRow,
  b: CaptureItemRow,
): number {
  return (
    a.systemName.localeCompare(b.systemName) ||
    a.subSystemName.localeCompare(b.subSystemName) ||
    a.assetTypeName.localeCompare(b.assetTypeName)
  )
}

// Pure merge of persisted site rows with the site template, mirroring
// the server's `assembleSessionDetail` site branch (merge-template.ts:344-362).
// Persisted rows project first and suppress their template slot
// (invariant I-API-15: isTemplateOnly === true iff no persisted row for
// that asset type in this session); remaining template types synthesise
// a template-only row. Template ids are de-duped. The combined list is
// sorted by `compareSiteItemRows`. Exported pure so the offline↔backend
// parity test can pin it to the server's documented output shape.
export function mergeSiteItems(
  persisted: readonly SiteItemSqlRow[],
  templates: readonly SiteTemplateSqlRow[],
): CaptureItemRow[] {
  const persistedAssetTypeIds = new Set<string>()
  const merged: CaptureItemRow[] = []
  for (const row of persisted) {
    merged.push(projectItem(row))
    persistedAssetTypeIds.add(row.asset_type_id)
  }
  const seenTemplate = new Set<string>()
  for (const row of templates) {
    if (seenTemplate.has(row.asset_type_id)) continue
    seenTemplate.add(row.asset_type_id)
    if (persistedAssetTypeIds.has(row.asset_type_id)) continue
    merged.push(projectTemplateOnly(row))
  }
  merged.sort(compareSiteItemRows)
  return merged
}

/**
 * Site-level capture items for a given session, with the `1 asset / site`
 * template merged in client-side so un-captured site infrastructure is
 * countable offline (H16 / #709). Returns rows in the `CaptureItemRow`
 * shape so `<AssetRow>` consumers don't need to change. Pass an empty
 * string to keep the hook idle (the SiteCaptureScreen does this until it
 * has both site metadata and an active session).
 */
export function useSiteItems(sessionId: string): UseSiteItemsResult {
  const itemsResult = useQuery<SiteItemSqlRow>(
    `SELECT
       ci.id,
       ci.asset_type_id,
       at.name AS asset_type_name,
       at.unit_of_measure,
       at.grouping_convention,
       at.is_field_added,
       at.requires_review,
       ass.id AS sub_system_id,
       ass.name AS sub_system_name,
       asys.id AS system_id,
       asys.name AS system_name,
       ci.quantity,
       ci.is_zero_confirmed,
       ci.notes,
       ci.captured_at,
       ci.updated_at,
       ci.sync_version
     FROM capture_items ci
     INNER JOIN asset_types at ON ci.asset_type_id = at.id
     INNER JOIN asset_sub_systems ass ON at.sub_system_id = ass.id
     INNER JOIN asset_systems asys ON ass.system_id = asys.id
     WHERE ci.capture_session_id = ?
       AND ci.building_id IS NULL
       AND ci.space_id IS NULL
       -- Mirror the server's canonical filter (get-session.ts:259): an admin
       -- Reject soft-deletes the row via capture_items.rejected_at. PR 713
       -- lets a rejected tombstone coexist with a live row in one slot; without
       -- this the offline mirror renders (and could duplicate) the rejected
       -- count as live. Sibling issue 708.
       AND ci.rejected_at IS NULL
       AND at.grouping_convention = ?`,
    [sessionId, GROUPING_CONVENTION.PER_SITE],
  )

  // Site template: every active `1 asset / site` asset type. Not scoped
  // to the session/site — site infrastructure types are contract-level,
  // so every site session shares the same template (matches the server's
  // contract-scoped loadSiteTemplateAssetTypeIds). The three
  // `rejected_at IS NULL` filters drop any admin-soft-deleted
  // type / sub-system / system from the template.
  const templateResult = useQuery<SiteTemplateSqlRow>(
    `SELECT
       at.id AS asset_type_id,
       at.name AS asset_type_name,
       at.unit_of_measure,
       at.grouping_convention,
       at.is_field_added,
       at.requires_review,
       ass.id AS sub_system_id,
       ass.name AS sub_system_name,
       asys.id AS system_id,
       asys.name AS system_name
     FROM asset_types at
     INNER JOIN asset_sub_systems ass ON at.sub_system_id = ass.id
     INNER JOIN asset_systems asys ON ass.system_id = asys.id
     WHERE at.grouping_convention = ?
       AND at.rejected_at IS NULL
       AND ass.rejected_at IS NULL
       AND asys.rejected_at IS NULL`,
    [GROUPING_CONVENTION.PER_SITE],
  )

  const siteItems = useMemo(
    () =>
      sessionId === ''
        ? []
        : mergeSiteItems(itemsResult.data, templateResult.data),
    [sessionId, itemsResult.data, templateResult.data],
  )

  if (sessionId === '') return IDLE

  return {
    siteItems,
    isLoading: itemsResult.isLoading || templateResult.isLoading,
    isFetching: itemsResult.isFetching || templateResult.isFetching,
    error: itemsResult.error ?? templateResult.error,
  }
}
